<template lang="pug">
.address-card-input(:class="{ 'hide-autocomplete-popper': numberOfFilledFields < 2 }")
    .address-card-inner
        .address-fields
            //- Personal data group: Gender, First name, Last name
            el-row.no-padding(v-if="showField('gender') || showField('first_name') || showField('last_name')" :gutter="20")
                el-col(v-if="showField('gender')" :span="6")
                    el-form-item(:label="$t('Gender')" :prop="getPropPath('gender')")
                        el-select.is-full-width(
                            v-model="gender"
                            :placeholder="getPlaceholder('gender')"
                        )
                            el-option(
                                v-for="item in genderOptions"
                                :key="item.value"
                                :label="$t(item.label)"
                                :value="item.value"
                            )
                el-col(v-if="showField('first_name')" :span="showField('gender') ? 9 : 12")
                    el-form-item(:label="$t('First name')" :prop="getPropPath('first_name')")
                        el-input(v-model="firstName" :placeholder="getPlaceholder('first_name')" :maxlength="255")
                el-col(v-if="showField('last_name')" :span="showField('gender') ? 9 : 12")
                    el-form-item(:label="$t('Last name')" :prop="getPropPath('last_name')")
                        el-input(v-model="lastName" :placeholder="getPlaceholder('last_name')" :maxlength="255")

            //- Company data group: Company, VAT ID
            el-row.no-padding(v-if="showField('company') || showField('tax_id')" :gutter="20")
                el-col(v-if="showField('company')" :span="showField('tax_id') ? 12 : 24")
                    el-form-item(:label="$t('Company')" :prop="getPropPath('company')")
                        el-input(v-model="company" :placeholder="getPlaceholder('company')" :maxlength="255")
                el-col(v-if="showField('tax_id')" :span="showField('company') ? 12 : 24")
                    el-form-item(:label="$t('VAT ID')" :prop="getPropPath('tax_id')")
                        el-input(v-model="taxId" :placeholder="getPlaceholder('tax_id')" :maxlength="255")

            //- Email field
            el-form-item(v-if="showField('email')" :label="$t('Email')" :prop="getPropPath('email')")
                el-input(v-model="email" :placeholder="getPlaceholder('email')" :maxlength="255")

            //- Phone field
            el-form-item(v-if="showField('phone')" :label="$t('Phone')" :prop="getPropPath('phone')")
                el-input(v-model="phone" :placeholder="getPlaceholder('phone')" :maxlength="255")

            //- Divider between contact/company info and address
            el-divider.fields-divider(v-if="hasContactFields && hasAddressFields")

            //- Street field
            el-form-item(v-if="showField('street')" :label="$t('Street')" :prop="getPropPath('street')")
                el-autocomplete.is-full-width(
                    v-model="street"
                    v-bind="autocompleteProps"
                    :placeholder="getPlaceholder('street')"
                    :debounce="300"
                    :fetch-suggestions="findAddresses"
                    @select="selectAddress"
                    @change="onFieldChange"
                    :maxlength="255"
                )

            //- Zipcode and City row
            el-row.no-padding(v-if="showField('zipcode') || showField('city')" :gutter="20")
                el-col(v-if="showField('zipcode')" :span="8")
                    el-form-item(:label="$t('Zipcode')" :prop="getPropPath('zipcode')")
                        el-autocomplete.is-full-width(
                            v-model="zipcode"
                            v-bind="autocompleteProps"
                            :placeholder="getPlaceholder('zipcode')"
                            :debounce="300"
                            :fetch-suggestions="findAddresses"
                            @select="selectAddress"
                            @change="onFieldChange"
                            :maxlength="255"
                        )
                el-col(v-if="showField('city')" :span="showField('zipcode') ? 16 : 24")
                    el-form-item(:label="$t('City')" :prop="getPropPath('city')")
                        el-autocomplete.is-full-width(
                            v-model="city"
                            v-bind="autocompleteProps"
                            :placeholder="getPlaceholder('city')"
                            :debounce="300"
                            :fetch-suggestions="findAddresses"
                            @select="selectAddress"
                            @change="onFieldChange"
                            :maxlength="255"
                        )

            //- Country field
            el-form-item.no-margin(v-if="showField('country')" :label="$t('Country')" :prop="getPropPath('country')")
                el-select.is-full-width(
                    v-model="country"
                    :placeholder="getPlaceholder('country') || $t('Select a country')"
                    filterable
                    @change="onFieldChange"
                )
                    el-option(
                        v-for="(label, code) in countriesList"
                        :key="code"
                        :label="label"
                        :value="code"
                    )

        .map-container(:class="{ show: showMap }")
            MglMap(
                ref="mapbox"
                :accessToken="accessToken"
                :mapStyle="mapStyle"
                :center="center"
                :zoom="zoom"
                :max-zoom="18"
                :min-zoom="2.5"
                :interactive="false"
                @load="mapBoxLoaded"
            )
                MglMarker(
                    v-if="marker"
                    :coordinates="marker"
                    :color="markerColor"
                    anchor="bottom"
                )
                    i.el-icon-fa-map-marker-alt.fas(slot="marker" :style="{ color: markerColor }")
</template>

<i18n>
{
    "de": {
        "Street": "Straße",
        "Zipcode": "PLZ",
        "City": "Stadt",
        "Country": "Land",
        "Select a country": "Land auswählen",
        "Company": "Firma",
        "VAT ID": "USt-IdNr.",
        "e.g. DE123456789": "z.B. DE123456789",
        "Gender": "Anrede",
        "First name": "Vorname",
        "Last name": "Nachname",
        "Select": "Auswählen",
        "Mr.": "Herr",
        "Mrs.": "Frau",
        "Diverse": "Divers",
        "Email": "E-Mail",
        "Phone": "Telefon"
    }
}
</i18n>

<script>
import axios from 'axios';
import Mapbox from 'mapbox-gl';
import MultiTouch from 'mapbox-gl-multitouch';
import { MglMap, MglMarker } from 'vue-mapbox';
import { getTime, startOfDay } from 'date-fns';
import _ from 'lodash';

import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
import de from 'i18n-iso-countries/langs/de.json';

countries.registerLocale(en);
countries.registerLocale(de);

import { country } from '@/filters';

export default {
    name: 'AddressCardInput',
    components: {
        MglMap,
        MglMarker
    },
    props: {
        form: {
            type: Object,
            required: true
        },
        formPath: {
            type: String,
            required: true
        },
        propPath: {
            type: String,
            default: ''
        },
        fields: {
            type: Array,
            default: () => ['street', 'zipcode', 'city', 'country']
        },
        placeholders: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
            germany: [10.4515, 51.1657],
            center: [10.4515, 51.1657],
            zoom: 3.6,
            showMap: false,
            markerColor: '#409EFF',
            genderOptions: [
                { value: 'M', label: 'Mr.' },
                { value: 'F', label: 'Mrs.' },
                { value: 'X', label: 'Diverse' }
            ]
        };
    },
    computed: {
        addressData() {
            return this.form[this.formPath];
        },
        countriesList() {
            return countries.getNames('de');
        },
        mapStyle() {
            return 'mapbox://styles/vanameland/ckclxo9az13jn1io5gnwr9tjr';
        },
        marker() {
            if (this.addressData.latitude && this.addressData.longitude) {
                return [this.addressData.longitude, this.addressData.latitude];
            }
            return undefined;
        },
        numberOfFilledFields() {
            let number = 0;
            if (this.addressData.street && this.addressData.street.length >= 2) number++;
            if (this.addressData.zipcode && this.addressData.zipcode.length >= 2) number++;
            if (this.addressData.city && this.addressData.city.length >= 2) number++;
            if (this.addressData.country) number++;
            return number;
        },
        autocompleteProps() {
            if (this.numberOfFilledFields >= 2) {
                return {
                    'trigger-on-focus': true,
                    'hide-loading': false,
                    'popper-append-to-body': true
                };
            }
            return {
                'trigger-on-focus': false,
                'hide-loading': true,
                'popper-append-to-body': false
            };
        },
        // Computed properties with getter/setter for form fields
        company: {
            get() {
                return this.addressData.company;
            },
            set(value) {
                this.addressData.company = value;
                this.$emit('field-change', { field: 'company', value });
            }
        },
        taxId: {
            get() {
                return this.addressData.tax_id;
            },
            set(value) {
                this.$set(this.addressData, 'tax_id', value);
                this.$emit('field-change', { field: 'tax_id', value });
            }
        },
        gender: {
            get() {
                return this.addressData.gender;
            },
            set(value) {
                this.addressData.gender = value;
                this.$emit('field-change', { field: 'gender', value });
            }
        },
        firstName: {
            get() {
                return this.addressData.first_name;
            },
            set(value) {
                this.addressData.first_name = value;
                this.$emit('field-change', { field: 'first_name', value });
            }
        },
        lastName: {
            get() {
                return this.addressData.last_name;
            },
            set(value) {
                this.addressData.last_name = value;
                this.$emit('field-change', { field: 'last_name', value });
            }
        },
        email: {
            get() {
                return this.addressData.email;
            },
            set(value) {
                this.addressData.email = value;
                this.$emit('field-change', { field: 'email', value });
            }
        },
        phone: {
            get() {
                return this.addressData.phone;
            },
            set(value) {
                this.$set(this.addressData, 'phone', value);
                this.$emit('field-change', { field: 'phone', value });
            }
        },
        hasContactFields() {
            const contactFields = ['gender', 'first_name', 'last_name', 'email', 'phone', 'company', 'tax_id'];
            return contactFields.some(field => this.fields.includes(field));
        },
        hasAddressFields() {
            const addressFields = ['street', 'zipcode', 'city', 'country'];
            return addressFields.some(field => this.fields.includes(field));
        },
        street: {
            get() {
                return this.addressData.street;
            },
            set(value) {
                this.addressData.street = value;
                this.$emit('field-change', { field: 'street', value });
            }
        },
        zipcode: {
            get() {
                return this.addressData.zipcode;
            },
            set(value) {
                this.addressData.zipcode = value;
                this.$emit('field-change', { field: 'zipcode', value });
            }
        },
        city: {
            get() {
                return this.addressData.city;
            },
            set(value) {
                this.addressData.city = value;
                this.$emit('field-change', { field: 'city', value });
            }
        },
        country: {
            get() {
                return this.addressData.country;
            },
            set(value) {
                this.addressData.country = value;
                this.$emit('field-change', { field: 'country', value });
            }
        }
    },
    watch: {
        marker(fresh, old) {
            let zoom = 3.6;
            let center = this.germany;

            if (fresh && fresh !== old) {
                zoom = 14;
                center = fresh;
            }

            if (this.$refs.mapbox && this.$refs.mapbox.map) {
                this.$refs.mapbox.map.flyTo({
                    center,
                    zoom,
                    essential: true,
                    duration: 0
                });
            }
        }
    },
    created() {
        this.mapbox = Mapbox;
    },
    methods: {
        showField(fieldName) {
            return this.fields.includes(fieldName);
        },

        getPlaceholder(fieldName) {
            return this.placeholders[fieldName] || '';
        },

        getPropPath(fieldName) {
            if (!this.propPath) return undefined;
            return `${this.propPath}.${fieldName}`;
        },

        clearCoordinates() {
            this.center = this.germany;
            this.addressData.latitude = null;
            this.addressData.longitude = null;
        },

        setCoordinates(longitude, latitude) {
            if (!longitude || !latitude) {
                this.center = this.germany;
            } else {
                this.center = [longitude, latitude];
            }
            this.addressData.longitude = longitude;
            this.addressData.latitude = latitude;
        },

        findAddresses(query, cb) {
            if (this.numberOfFilledFields >= 2) {
                const language = 'de';
                const timestamp = getTime(startOfDay(new Date()));
                const proximity = this.germany.join(',');

                let parts = [];
                if (this.addressData.street && this.addressData.street.length >= 2) {
                    parts.push(this.addressData.street);
                }
                if (this.addressData.zipcode && this.addressData.zipcode.length >= 2) {
                    parts.push(this.addressData.zipcode);
                }
                if (this.addressData.city && this.addressData.city.length >= 2) {
                    parts.push(this.addressData.city);
                }
                if (this.addressData.country) {
                    parts.push(country(this.addressData.country, language));
                }

                const searchQuery = parts.join(' ');
                const url = `${process.env.VUE_APP_MAPBOX_GOECODING_ENDPOINT}${searchQuery}.json?access_token=${process.env.VUE_APP_MAPBOX_TOKEN}&cachebuster=${timestamp}&autocomplete=true&language=${language}&types=postcode,address&proximity=${proximity}`;

                axios
                    .get(url)
                    .then(({ data }) => {
                        let suggestions = [];
                        if (data && data.features && data.features.length) {
                            data.features.forEach(feature => {
                                if (feature.place_name &&
                                    (feature.place_type.includes('address') || feature.place_type.includes('postcode')) &&
                                    feature.relevance >= 0.5
                                ) {
                                    suggestions.push({ value: feature.place_name, data: feature });
                                }
                            });
                        }
                        cb(suggestions);
                    })
                    .catch(() => cb([]));
            } else {
                cb([]);
            }
        },

        onFieldChange: _.debounce(function() {
            this.refreshMarker();
        }, 300),

        refreshMarker() {
            if (this.numberOfFilledFields < 2) return;

            this.findAddresses('', items => {
                if (items && items.length) {
                    const match = items[0];
                    if (match.data.relevance > 0.95) {
                        // High confidence - auto-fill
                        this.applyAddressData(match.data);
                    } else if (match.data.relevance > 0.7 && match.data.center) {
                        // Medium confidence - just set marker
                        this.setCoordinates(match.data.center[0], match.data.center[1]);
                    } else {
                        this.clearCoordinates();
                    }
                }
            });
        },

        selectAddress(address) {
            if (address.data) {
                this.applyAddressData(address.data);
            }
        },

        applyAddressData(data) {
            if (!data.context || !data.context.length) return;

            if (data.id.startsWith('address')) {
                if (data.address) {
                    this.street = _.trim(`${data.text} ${data.address}`);
                } else {
                    this.street = data.text;
                }
            } else if (data.id.startsWith('postcode')) {
                this.zipcode = data.text;
            }

            data.context.forEach(entry => {
                if (entry.id.startsWith('postcode')) {
                    this.zipcode = entry.text;
                } else if (entry.id.startsWith('place')) {
                    this.city = entry.text;
                } else if (entry.id.startsWith('country')) {
                    this.country = countries.getAlpha2Code(entry.text, 'de');
                }
            });

            if (data.id.startsWith('address') && data.center) {
                this.addressData.longitude = data.center[0];
                this.addressData.latitude = data.center[1];

                if (this.$refs.mapbox && this.$refs.mapbox.map) {
                    this.$refs.mapbox.map.flyTo({
                        center: data.center,
                        essential: true,
                        zoom: 14,
                        duration: 0
                    });
                }
            }
        },

        fixMapCenterAndZoom() {
            if (this.numberOfFilledFields === 4 && !this.marker) {
                this.refreshMarker();
            } else if (this.marker) {
                this.center = this.marker;
                this.zoom = 14;
            }
        },

        async mapBoxLoaded(event) {
            const map = event.map;
            this.fixMapCenterAndZoom();
            setTimeout(() => this.showMap = true, 300);
            map.addControl(new MultiTouch());
        }
    }
};
</script>

<style lang="sass" scoped>
.address-card-input
    container-type: inline-size
    border: 1px solid #ebeef5
    border-radius: 6px

    .address-card-inner
        display: flex
        flex-direction: column

    .address-fields
        order: 2
        padding: 15px
        background: #fafafa
        border-top: 1px solid #ebeef5

    .map-container
        order: 1
        width: 100%
        height: 160px
        background: $color-skeleton
        overflow: hidden
        border-radius: 6px 6px 0 0

        .mgl-map-wrapper
            opacity: 0
            transition: opacity .3s ease
            width: 100%
            height: 100%

        &.show
            .mgl-map-wrapper
                opacity: 1

        .mapboxgl-marker
            font-size: 2rem
            line-height: 2rem
            height: 2rem
            &:after
                content: ''
                position: absolute
                width: 10px
                height: 0px
                bottom: -2px
                left: calc(50% - 5px)
                box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.8)

    // Container query for wide layouts
    @container (min-width: 800px)
        .address-card-inner
            flex-direction: row

        .address-fields
            order: 1
            flex: 1
            border-top: none
            border-right: 1px solid #ebeef5
            border-radius: 6px 0 0 6px

        .map-container
            order: 2
            flex: 1
            height: auto
            min-height: 200px
            border-radius: 0 6px 6px 0

.hide-autocomplete-popper
    ::v-deep
        .el-autocomplete-suggestion
            display: none !important

.no-padding
    padding: 0

.is-full-width
    width: 100%

.fields-divider
    margin: 10px 0

body .el-input input[maxlength="255"]
    padding-right: 15px
</style>
